import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";

interface FaqItem {
  q: string;
  a: string;
}

// IntersectionObserver one-shot — añade .is-visible para reproducir la
// entrada por scroll. Mismo patrón que Testimonials/ForChainsTeaser.
function useReveal() {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function FAQ() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { ref, visible } = useReveal();
  const items =
    (t("faq.items", { returnObjects: true }) as unknown as FaqItem[]) || [];

  return (
    <section
      id="faq"
      ref={ref}
      className={`playium-faq${visible ? " is-visible" : ""}`}
    >
      <div className="playium-faq__inner">
        <div className="playium-faq__header">
          <h2 className="playium-faq__title playium-heading">
            {t("faq.title")}
          </h2>
        </div>

        <div className="playium-faq__list">
          {items.map((faq, i) => {
            const isOpen = openFaq === i;
            return (
              <article
                key={i}
                className={`playium-faq__item${isOpen ? " is-open" : ""}`}
                style={{ ["--reveal-delay" as never]: `${i * 60}ms` }}
              >
                <button
                  type="button"
                  className="playium-faq__question"
                  aria-expanded={isOpen}
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                >
                  <span className="playium-faq__question-text">{faq.q}</span>
                  <span
                    className="playium-faq__toggle"
                    aria-hidden="true"
                  >
                    <Plus />
                  </span>
                </button>

                {/* Grid-rows trick (0fr → 1fr) — animación de altura sin
                    necesidad de medir el DOM, manteniendo el contenido en
                    el flow normal y respetando el padding. */}
                <div className="playium-faq__answer">
                  <div className="playium-faq__answer-inner">
                    <p className="playium-faq__answer-text">{faq.a}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
